import type { IChoiceGroupOption, IColumn, IStackTokens } from "@fluentui/react";
import {
    Checkbox,
    ChoiceGroup,
    DetailsList,
    DetailsListLayoutMode,
    DetailsRow,
    IconButton,
    Position,
    PrimaryButton,
    SelectionMode,
    Separator,
    SpinButton,
    Stack,
} from "@fluentui/react";
import React, { useEffect, useState, type FormEvent } from "react";
import type { NodeProps, Node } from "@xyflow/react";
import type { ExtraNodeData, InterfaceToRecord } from "./action-config-node";
import ActionConfigNode, { zoneOptions } from "./action-config-node";
import type { AntennaWithCanId, ObjectsInAntennaZoneData } from "../../../api";
import { Antenna, Zone } from "../../../api";
import { useTranslation } from "react-i18next";
import colors from "../../colors.scss";
import zIndex from "../../z-index.scss";
import { useUpdateNodeData } from "@/lib/hooks/reactflow";

type ObjectsInAntennaZoneNode = Node<InterfaceToRecord<ObjectsInAntennaZoneData & ExtraNodeData>>;

const stackTokens: IStackTokens = { childrenGap: 20 };

export default function ObjectsInAntennaZone(
    props: NodeProps<ObjectsInAntennaZoneNode>,
): JSX.Element {
    const { t } = useTranslation();
    const [antennas, setAntennas] = useState(
        props.data.antennas ?? [{ antenna: Antenna.A, canId: 0 }],
    );
    const [selectedZone, setSelectedZone] = useState(props.data.zone ?? Zone.ONE);
    const [pedestrian, setPedestrian] = useState(props.data.pedestrian ?? true);
    const [vehicle, setVehicle] = useState(props.data.vehicle ?? true);
    const [station, setStation] = useState(props.data.station ?? true);
    const [selectedAntenna, setSelectedAntenna] = useState<Antenna>(Antenna.A);
    const [selectedCanId, setSelectedCanId] = useState(0);
    const updateNode = useUpdateNodeData<ObjectsInAntennaZoneNode>(props.id);

    // We have to set the data to prevent undefined values
    useEffect(() => {
        updateNode({ zone: selectedZone, pedestrian, vehicle, station, antennas });
    }, [selectedZone, antennas, pedestrian, vehicle, station, updateNode]);

    function onZoneChange(
        _e?: FormEvent<HTMLElement | HTMLInputElement>,
        option?: IChoiceGroupOption,
    ): void {
        if (!option) {
            return;
        }
        setSelectedZone(option.key as Zone);
    }

    function renderItem(item: AntennaWithCanId, column: IColumn): JSX.Element {
        switch (column.key) {
            case "antenna":
                return <span>{item.antenna}</span>;
            case "canId":
                return <span>{item.canId}</span>;
            case "action":
                return (
                    <IconButton
                        styles={{
                            rootHovered: {
                                color: colors["secondarydashboard-severe"],
                            },
                        }}
                        iconProps={{
                            iconName: "Delete",
                        }}
                        // Remove the antenna of this row from the selected antennas.
                        onClick={() => {
                            setAntennas(antennas.filter((antenna) => antenna != item));
                        }}
                    />
                );
            default:
                return <></>;
        }
    }

    function renderCommand(column: IColumn): JSX.Element {
        switch (column.key) {
            case "antenna":
                return (
                    <ChoiceGroup
                        label={t("component.actionConfigNodes.objectsInAntennaZone.selectAntenna")}
                        styles={{ flexContainer: { display: "flex", gap: 20 } }}
                        options={[
                            { key: Antenna.A, text: Antenna.A },
                            { key: Antenna.B, text: Antenna.B },
                        ]}
                        selectedKey={selectedAntenna}
                        onChange={(_e, option) => setSelectedAntenna(option?.key as Antenna)}
                    />
                );
            case "canId":
                return (
                    <SpinButton
                        label={t("component.actionConfigNodes.objectsInAntennaZone.selectCanId")}
                        labelPosition={Position.top}
                        className={"nodrag"}
                        onChange={(_e, canId) => {
                            if (!canId) {
                                return;
                            }
                            setSelectedCanId(parseInt(canId));
                        }}
                        min={0}
                        max={3}
                    />
                );
            case "action":
                return (
                    <PrimaryButton
                        iconProps={{ iconName: "Add" }}
                        text={t("component.actionConfigNodes.objectsInAntennaZone.iconButton.add")}
                        onClick={() => {
                            if (selectedAntenna) {
                                setAntennas([
                                    { antenna: selectedAntenna, canId: selectedCanId },
                                    ...antennas.filter(
                                        (antenna) =>
                                            antenna.antenna !== selectedAntenna ||
                                            antenna.canId !== selectedCanId,
                                    ),
                                ]);
                            }
                        }}
                    />
                );
            default:
                return <></>;
        }
    }

    return (
        <ActionConfigNode nodeKind={props.type} {...props.data}>
            <ChoiceGroup
                label={t("component.actionConfigNodes.objectsInAntennaZone.selectZoneAndObjects")}
                styles={{ flexContainer: { display: "flex", gap: 20 } }}
                className="nodrag"
                options={zoneOptions(t)}
                selectedKey={selectedZone}
                onChange={onZoneChange}
            />
            <Stack horizontal tokens={stackTokens} className="nodrag">
                <Checkbox
                    label={t("component.actionConfigNodes.anyInZone.pedestrian")}
                    checked={pedestrian}
                    onChange={(_e, checked) => setPedestrian(checked!)}
                />
                <Checkbox
                    label={t("component.actionConfigNodes.anyInZone.vehicle")}
                    checked={vehicle}
                    onChange={(_e, checked) => setVehicle(checked!)}
                />
                <Checkbox
                    label={t("component.actionConfigNodes.anyInZone.station")}
                    checked={station}
                    onChange={(_e, checked) => setStation(checked!)}
                />
            </Stack>
            <Separator />
            <DetailsList
                styles={{
                    root: { maxWidth: 450, height: 300 },
                    headerWrapper: {
                        position: "sticky",
                        top: "0",
                        zIndex: `${zIndex.listHeader}`,
                    },
                }}
                selectionMode={SelectionMode.none}
                className="nodrag nowheel"
                items={antennas}
                onRenderItemColumn={(item: AntennaWithCanId, _index, column) =>
                    renderItem(item, column!)
                }
                onRenderDetailsHeader={(props, defaultRender) => {
                    if (!props || !defaultRender) {
                        return <></>;
                    }
                    return (
                        <>
                            <DetailsRow
                                styles={{
                                    fields: { display: "flex", alignItems: "end" },
                                    root: {
                                        "&:hover": {
                                            background: "white",
                                        },
                                    },
                                }}
                                item={{}}
                                itemIndex={0}
                                onRenderItemColumn={(_item, _index, column) =>
                                    renderCommand(column!)
                                }
                                selectionMode={SelectionMode.none}
                                columns={props.columns}
                            />
                            {defaultRender(props)}
                        </>
                    );
                }}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                columns={[
                    {
                        name: t("component.actionConfigNodes.objectsInAntennaZone.antenna"),
                        key: "antenna",
                        minWidth: 50,
                        maxWidth: 100,
                    },
                    {
                        name: t("component.actionConfigNodes.objectsInAntennaZone.canId"),
                        key: "canId",
                        minWidth: 50,
                        maxWidth: 100,
                    },
                    {
                        name: t(
                            "component.actionConfigNodes.objectsInAntennaZone.list.delete.name",
                        ),
                        key: "action",
                        minWidth: 100,
                        maxWidth: 150,
                    },
                ]}
            />
        </ActionConfigNode>
    );
}
